import React, { useMemo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, Flex, TextV2 } from '@withjoy/joykit';

import { UserNote } from './UserNote';
import { HotelCTA } from './HotelCTA';
import { useTranslation } from '@shared/core';
import { CostBreakdown } from './CostBreakdown';
import { StarRating, UserRating } from '@apps/stays/components/Ratings';
import { TravelLogistics } from './TravelLogistics';
import { OtaProviderName } from './OtaProviderName';
import { withWindow } from '@shared/utils/withWindow';
import { Skeleton } from '@shared/components/Skeleton';
import { getValidCoordinates } from '@shared/core/places';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { SOLD_OUT_CTA_ALLOWED_SOURCES, HotelSearchSource } from '@shared/constants/accommodations';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { SoldOutTag } from '@shared/components/SoldOutTag/SoldOutTag';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';
import { SponsoredTag } from './SponsoredTag';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import { getSelectedRedirectTelemetryName, getSelectedRedirectTravelOfferProvider } from '@shared/utils/extractHotelRedirects';
import type { AccommodationListingDTO, AccommodationsConsoleState, StandardHotelPricingValues, HotelRoomBlockPricingValues } from '@shared/types/Accommodations.types';
import type { AccommodationsListingMobileLayoutState } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';
import { useAccommodationRedirectUrl, createUtmParametersForAccommodationsConsolePage } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationRedirectUrl';

type ListingEntryMobileV2Props = Readonly<{
  noOfNights: number;
  listing: AccommodationListingDTO;
  state: AccommodationsConsoleState;
  variant: AccommodationsListingMobileLayoutState['variant'];
  sourceName: HotelSearchSource;
  showRoomBlockNoteAndDate: boolean;
  renderListingCta?: (listing: AccommodationListingDTO, redirectUrl?: string) => React.ReactNode;
  /** Optional overlay badge on the photo (e.g. the cash-fund "Honeymoon Rate" tag). */
  imageBadge?: React.ReactNode;
  /** Optional cost-area override (e.g. the cash-fund "Fund covers / +$X" breakdown). Falls back to CostBreakdown. */
  renderCostBreakdown?: (pricingInfo: StandardHotelPricingValues | HotelRoomBlockPricingValues) => React.ReactNode;
  /** Optional CTA background override (e.g. the cash-fund gold). */
  ctaBackgroundColor?: string;
}>;

const ListingEntryMobileV2 = ({
  listing,
  state,
  noOfNights,
  variant,
  sourceName,
  showRoomBlockNoteAndDate,
  renderListingCta,
  imageBadge,
  renderCostBreakdown,
  ctaBackgroundColor,
}: ListingEntryMobileV2Props) => {
  const parseUrl = useSafeUrlParser();
  const getRedirectUrl = useAccommodationRedirectUrl(state, sourceName);
  const telemetry = useListingsPanelTelemetry();
  const { tq } = useTranslation('accommodationsConsole');

  const isSoldOut = listing.type === 'standardHotel' && !!listing.isSoldOutForSelectedDates;
  // Same resolution the redirect URL uses, so the Amplitude event and the URL can't disagree.
  const mergedUtm = useAcquisitionAwareUtm(createUtmParametersForAccommodationsConsolePage(sourceName));

  // Treatment lets a sold-out tile open the PDP. Scoped to the guest/admin search view so only those tiles are bucketed.
  const isSoldOutPdpScopedSource = sourceName === HotelSearchSource.GUEST_MAP || sourceName === HotelSearchSource.ADMIN_MAP;
  const [isSoldOutPdpEnabled] = useIsFeatureEnabled('accommodationsSoldOutPdpExperiment', { skip: !isSoldOut || !isSoldOutPdpScopedSource });
  const isLinkEnabled = !isSoldOut || isSoldOutPdpEnabled;

  // Treatment routes a sold-out tile to the hotel PDP by skipping the interceptor on the redirect page.
  const redirectUrl = getRedirectUrl(listing, { skipAvailabilityCheck: isSoldOut && isSoldOutPdpEnabled });

  const handleNavigation = useEventCallback(() => {
    telemetry.seeAvailabilityClicked({
      joyPlaceId: listing.joyPlaceId || '',
      hotelName: listing.name,
      isSponsored: listing.isSponsored ?? false,
      chain: listing.chain,
      isCoupleFavourite: listing.isFavorite ?? false,
      priceSource: listing.pricingInfo.source,
      travelOfferProvider: getSelectedRedirectTravelOfferProvider(listing.redirectMechanisms),
    });

    if (redirectUrl) {
      const urlInfo = parseUrl(redirectUrl, 'ListingEntryMobileV2.handleNavigation');
      if (urlInfo) {
        telemetry.hotelBookingRedirect({
          hotelName: listing.name,
          destinationURL: redirectUrl,
          destinationDomain: urlInfo.hostname,
          joyPlaceId: listing.joyPlaceId || '',
          destinationName: getSelectedRedirectTelemetryName(listing.redirectMechanisms),
          isSponsored: listing.isSponsored ?? false,
          chain: listing.chain,
          isCoupleFavourite: listing.isFavorite ?? false,
          ...mergedUtm,
        });
      }
      withWindow(global => global.open(redirectUrl, '_blank'));
    }
  });

  const pointOfInterest = useMemo(() => {
    const location = getValidCoordinates(state.circularRegionSearch);
    if (!location) return null;

    let displayName = state.searchLocation || tq`venue`;
    if (state.searchLocation === state.venue.name) displayName = tq`venue`;

    return { displayName, location };
  }, [state.circularRegionSearch, state.searchLocation, state.venue.name, tq]);

  const shouldShowCTAForSoldOut = SOLD_OUT_CTA_ALLOWED_SOURCES.includes(sourceName);

  const shouldShowHotelCTAButton =
    shouldShowCTAForSoldOut ||
    (variant === 'treatment_cta_button' && redirectUrl && !isSoldOut && ((listing.type === 'standardHotel' && !listing.isSoldOutForSelectedDates) || listing.type === 'roomBlock'));

  const isClickable = variant === 'treatment_clickable_card' && isLinkEnabled;
  const listingBadgeText = listing.type === 'roomBlock' ? tq`🛌 Room Block` : listing.isPickedByCouple ? tq`🏩 Couple's Pick` : null;

  const images = useMemo(
    () => (listing.mediaAssets?.photos?.length ? listing.mediaAssets.photos.map(p => ({ src: p.url, alt: listing.name })) : [{ src: DEFAULT_HOTEL_IMAGE, alt: listing.name }]),
    [listing.mediaAssets?.photos, listing.name],
  );

  return (
    <Flex
      gap={2}
      as="button"
      paddingY={4}
      flexDirection="row"
      cursor={isClickable ? 'pointer' : 'default'}
      onClick={isClickable ? handleNavigation : undefined}
    >
      <HotelPhotoCarousel
        hotel={listing}
        images={images}
        navigation={{
          variant: 'minimal',
          visible: 'default',
        }}
        context="Accommodations.ListingEntryMobileV2"
        borderRadius={12}
        width={140}
        flexShrink={0}
        alignSelf="stretch"
      >
        {listingBadgeText && !imageBadge && (
          <TextV2
            color="mono14"
            top={pxToRem(9)}
            left={pxToRem(6)}
            position="absolute"
            paddingX={pxToRem(8)}
            paddingY={pxToRem(4)}
            borderRadius={pxToRem(12)}
            typographyVariant="label1"
            backgroundColor="rgba(255, 255, 255, 0.72)"
          >
            {listingBadgeText}
          </TextV2>
        )}
        {imageBadge && (
          <Box
            position="absolute"
            top={pxToRem(9)}
            left={pxToRem(6)}
          >
            {imageBadge}
          </Box>
        )}
        {listing.isSponsored && <SponsoredTag />}
      </HotelPhotoCarousel>

      <Flex
        gap={2}
        flex={1}
        paddingLeft={3}
        flexDirection="column"
        justifyContent="space-between"
      >
        <Flex
          flexDirection="column"
          gap={3}
        >
          <Flex
            flexDirection="column"
            gap={2}
          >
            <TextV2
              color="mono14"
              textAlign="start"
              typographyVariant="hed1"
            >
              {listing.name}
            </TextV2>

            {listing.starRating && <StarRating rating={listing.starRating} />}
          </Flex>

          {listing.userRating && (
            <UserRating
              rating={listing.userRating.ratingValue}
              reviewCount={listing.userRating.ratingCount}
            />
          )}

          {pointOfInterest && (
            <TravelLogistics
              listing={listing}
              useShortWording={true}
              pointOfInterest={pointOfInterest}
            />
          )}

          <OtaProviderName otaProvider={listing.otaProvider} />

          {listing.type === 'roomBlock' && listing.userNote && showRoomBlockNoteAndDate && <UserNote note={listing.userNote} />}
        </Flex>

        <Flex flexDirection="column">
          <Box paddingY={5}>
            {listing.pricingInfo.isLoading ? (
              <CostBreakdown.Skeleton />
            ) : listing.pricingInfo.values ? (
              renderCostBreakdown ? (
                renderCostBreakdown(listing.pricingInfo.values)
              ) : (
                <CostBreakdown
                  noOfNights={noOfNights}
                  occupancies={state.occupancies}
                  pricingInfo={listing.pricingInfo.values}
                />
              )
            ) : null}
          </Box>

          {isSoldOut && (
            <Box marginBottom={5}>
              <SoldOutTag eventDate={createDateFromUnformattedString(state.searchDates.checkin)} />
            </Box>
          )}

          {shouldShowHotelCTAButton &&
            (renderListingCta ? (
              renderListingCta(listing, redirectUrl)
            ) : (
              <HotelCTA
                listing={listing}
                redirectUrl={redirectUrl}
                sourceName={sourceName}
                backgroundColor={ctaBackgroundColor}
              />
            ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

ListingEntryMobileV2.Skeleton = () => (
  <Flex
    gap={2}
    paddingY={4}
    flexDirection="row"
  >
    <Skeleton
      width={140}
      flexShrink={0}
      borderRadius={12}
      alignSelf="stretch"
    />

    <Flex
      gap={2}
      flex={1}
      paddingLeft={3}
      flexDirection="column"
      justifyContent="space-between"
    >
      <Flex
        flexDirection="column"
        gap={3}
      >
        <Flex
          gap={2}
          flexDirection="column"
        >
          <Skeleton
            height={20}
            width="80%"
          />

          <Flex
            alignContent="center"
            justifyContent="space-between"
          >
            <Skeleton
              height={16}
              width={80}
            />
            <Skeleton
              height={16}
              width={60}
            />
          </Flex>
        </Flex>

        <Skeleton
          height={16}
          width="50%"
        />

        <Skeleton
          height={14}
          width="60%"
        />
      </Flex>

      <Flex
        gap={2}
        flexDirection="column"
      >
        <CostBreakdown.Skeleton />
      </Flex>
    </Flex>
  </Flex>
);

export { ListingEntryMobileV2 };
